/**
 * AuthClient - HTTP wrappers around the server `/auth/*` endpoints.
 *
 * Cookies are sent and received via `credentials: "include"`; the browser
 * persists the session cookie automatically, so callers only need to await
 * each request.
 */
import type { AuthenticatedUser } from "@taskin-code/contracts";

const headers = { "Content-Type": "application/json" };

const baseUrl = (path: string): string => {
  if (typeof window === "undefined") return path;
  return path;
};

const parseError = async (response: Response, fallback: string): Promise<string> => {
  try {
    const body = await response.json();
    if (
      body &&
      typeof body === "object" &&
      typeof (body as { error?: string }).error === "string"
    ) {
      return (body as { error: string }).error;
    }
  } catch {
    // ignore
  }
  return fallback;
};

export interface LoginResult {
  readonly user: AuthenticatedUser;
}

/**
 * A password can end the login in one of two states: signed in, or owing a
 * second factor. Callers must branch on `mfaRequired` rather than assume a
 * user came back.
 */
export type LoginOutcome =
  | { readonly kind: "authenticated"; readonly user: AuthenticatedUser }
  | { readonly kind: "mfa-required"; readonly challenge: string };

/** Mirrors `TotpStatus` in apps/server/src/auth/Services/TotpService.ts. */
export interface TotpStatus {
  readonly activated: boolean;
  /** A secret exists but was never confirmed with a live code. */
  readonly pendingSetup: boolean;
  readonly activatedAt: string | null;
  readonly remainingRecoveryCodes: number;
}

export interface TotpEnrollment {
  readonly secret: string;
  readonly otpauthUri: string;
}

export const AuthClient = {
  async login(email: string, password: string): Promise<LoginOutcome> {
    const response = await fetch(baseUrl("/auth/login"), {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({ email, password }),
    });
    if (!response.ok) {
      throw new Error(await parseError(response, "Login failed"));
    }
    const body = (await response.json()) as Partial<LoginResult> & {
      mfaRequired?: boolean;
      challenge?: string;
    };
    if (body.mfaRequired === true && typeof body.challenge === "string") {
      return { kind: "mfa-required", challenge: body.challenge };
    }
    if (!body.user) {
      throw new Error("Login failed");
    }
    return { kind: "authenticated", user: body.user };
  },

  /** Second leg of a two-factor login. Pass exactly one of `code`/`recoveryCode`. */
  async completeTotpLogin(input: {
    readonly challenge: string;
    readonly code?: string;
    readonly recoveryCode?: string;
  }): Promise<LoginResult> {
    const response = await fetch(baseUrl("/auth/login/totp"), {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      throw new Error(await parseError(response, "Invalid code"));
    }
    return (await response.json()) as LoginResult;
  },

  async totpStatus(): Promise<TotpStatus> {
    const response = await fetch(baseUrl("/auth/totp/status"), {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error(await parseError(response, "Failed to load two-factor status"));
    }
    return (await response.json()) as TotpStatus;
  },

  /** Starts enrollment. Nothing is enforced until `totpActivate` succeeds. */
  async totpSetup(): Promise<TotpEnrollment> {
    const response = await fetch(baseUrl("/auth/totp/setup"), {
      method: "POST",
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error(await parseError(response, "Failed to start setup"));
    }
    return (await response.json()) as TotpEnrollment;
  },

  /** Confirms the authenticator works and returns the one-time recovery codes. */
  async totpActivate(code: string): Promise<ReadonlyArray<string>> {
    const response = await fetch(baseUrl("/auth/totp/activate"), {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({ code }),
    });
    if (!response.ok) {
      throw new Error(await parseError(response, "That code did not match"));
    }
    const body = (await response.json()) as { recoveryCodes: ReadonlyArray<string> };
    return body.recoveryCodes;
  },

  async totpDisable(password: string): Promise<void> {
    const response = await fetch(baseUrl("/auth/totp/disable"), {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({ password }),
    });
    if (!response.ok) {
      throw new Error(await parseError(response, "Failed to disable two-factor"));
    }
  },

  async totpRegenerateRecoveryCodes(): Promise<ReadonlyArray<string>> {
    const response = await fetch(baseUrl("/auth/totp/recovery-codes"), {
      method: "POST",
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error(await parseError(response, "Failed to regenerate recovery codes"));
    }
    const body = (await response.json()) as { recoveryCodes: ReadonlyArray<string> };
    return body.recoveryCodes;
  },

  async logout(): Promise<void> {
    await fetch(baseUrl("/auth/logout"), {
      method: "POST",
      credentials: "include",
    });
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const response = await fetch(baseUrl("/auth/change-password"), {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    if (!response.ok) {
      throw new Error(await parseError(response, "Failed to change password"));
    }
  },

  /**
   * Rename the signed-in user.
   *
   * Name only: the email address is the login identifier and only an
   * administrator may change it, so there is no parameter for it here. Returns
   * the stored (trimmed) name, which is what the caller writes into the auth
   * store — echoing the server's version keeps the UI from showing the raw
   * input when normalization changed it.
   */
  async updateProfileName(name: string): Promise<string> {
    const response = await fetch(baseUrl("/auth/profile"), {
      method: "PATCH",
      credentials: "include",
      headers,
      body: JSON.stringify({ name }),
    });
    if (!response.ok) {
      throw new Error(await parseError(response, "Failed to save your name"));
    }
    const body = (await response.json()) as { name: string };
    return body.name;
  },

  /**
   * Replace the signed-in user's profile image.
   *
   * The image travels as a base64 data URL inside JSON rather than as
   * `multipart/form-data`, because that is the shape the browser hands over —
   * `FileReader.readAsDataURL` — and because every other call in this file is
   * already JSON with the same error envelope. The cost is the ~33% base64
   * overhead on a file capped at 5 MB.
   *
   * Returns the stored file name, which is what the caller writes into the
   * auth store so every surface repaints without another round trip.
   */
  async setAvatar(dataUrl: string): Promise<string> {
    const response = await fetch(baseUrl("/auth/profile/avatar"), {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({ dataUrl }),
    });
    if (!response.ok) {
      throw new Error(await parseError(response, "Failed to upload the photo"));
    }
    const body = (await response.json()) as { avatarFileName: string };
    return body.avatarFileName;
  },

  async removeAvatar(): Promise<void> {
    const response = await fetch(baseUrl("/auth/profile/avatar"), {
      method: "DELETE",
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error(await parseError(response, "Failed to remove the photo"));
    }
  },

  async me(): Promise<AuthenticatedUser | null> {
    const response = await fetch(baseUrl("/auth/me"), {
      method: "GET",
      credentials: "include",
    });
    if (response.status === 401) return null;
    if (!response.ok) {
      throw new Error(await parseError(response, "Failed to load session"));
    }
    const body = (await response.json()) as { user: AuthenticatedUser };
    return body.user;
  },
};
